import { Season, SeasonRanking, SeasonRankingUI } from '@/model/ranking/Season'
import {
  sliceAndSortRankings,
  sortUsersByRank,
  formatWinnings,
  sortTournamentsById,
} from '@/store/modules/ranking/index'
import { State } from '@/store/state'
import { ERROR_CODES } from '@/model/ErrorCodes'
import { formatMoneyAmount } from '@/lib/formattingUtils'

export interface TicketGroup {
  tournamentName: string
  seasonRankings: Array<SeasonRankingUI>
}

export interface SeasonRankingState {
  current: Season
}

const state: SeasonRankingState = {
  current: {
    lastQualifiedRank: 0,
    myRank: {
      playerId: '',
      playerName: '',
      seasonRank: 0,
      seasonScore: 0,
      ticketInfo: null,
      seasonPrize: 0,
    },
    seasonRankings: [],
    zoneId: '',
  },
}

export const getters = {
  /**
   * All rankings
   * Can be sliced by providing (the first two) parameters
   * Search filter will be applied if term is provided via parameter
   * Sorted by season-rank (in ascending order)
   * Winnings will be currency-formatted
   **/
  rankings:
    (state: SeasonRankingState, getters: any, rootState: State) =>
    (from?: number, to?: number, search = ''): Array<SeasonRankingUI> => {
      try {
        // console.time('compute table data -> overall')
        // Spread operator used to clone the original array inside the state
        const searchExpression = new RegExp(search.toLowerCase())
        const searchResults =
          search.length > 0
            ? state.current.seasonRankings.filter((item) => searchExpression.test(item.playerName.toLowerCase()))
            : [...state.current.seasonRankings]

        const rankings = sliceAndSortRankings(searchResults, from, to, sortUsersByRank) as Array<SeasonRanking>
        const result = rankings.map((ranking) => ({
          ...ranking,
          seasonPrize:
            (ranking.seasonPrize as any) > 0
              ? formatMoneyAmount(ranking.seasonPrize as string, rootState.user.currency)
              : '-',
          seasonRank: (ranking.seasonRank as any) > 0 ? ranking.seasonRank : '-',
        }))
        // const result = formatWinnings(ranksReplaced, rootState.user.currency) as Array<SeasonRanking>
        // console.timeEnd('compute table data -> overall')
        return result as Array<SeasonRankingUI>
      } catch (err) {
        return []
      }
    },
  /**
   * Exclude users qualified by ticket (i.e. finished a weekly tournament as 1st, end or 3rd)
   * Can be sliced by providing (the first two) parameters
   * Search filter will be applied if term is provided via parameter
   * Sorted by season-rank (in ascending order)
   * Winnings will be currency-formatted
   **/
  rankingsHighScoresOnly:
    (state: SeasonRankingState, getters: any, rootState: State) =>
    (from?: number, to?: number, search = ''): Array<SeasonRankingUI> => {
      try {
        // Spread operator used to clone the original array inside the state
        // console.time('compute table data -> scores only')
        const searchExpression = new RegExp(search.toLowerCase())
        const filteredList = [...state.current.seasonRankings.filter((item) => item.ticketInfo === null)]
        const searchResults =
          search.length > 0
            ? filteredList.filter((item) => searchExpression.test(item.playerName.toLowerCase()))
            : filteredList
        const rankings = sliceAndSortRankings(searchResults, from, to, sortUsersByRank) as Array<SeasonRanking>
        const result = rankings.map((ranking) => ({
          ...ranking,
          seasonPrize:
            (ranking.seasonPrize as any) > 0
              ? formatMoneyAmount(ranking.seasonPrize as string, rootState.user.currency)
              : '-',
          seasonRank: (ranking.seasonRank as any) > 0 ? ranking.seasonRank : '-',
        }))
        // console.timeEnd('compute table data -> scores only')
        return result as Array<SeasonRankingUI>
      } catch (err) {
        return []
      }
    },
  /**
   * Only users qualified by ticket (i.e. finished a weekly tournament as 1st, end or 3rd) grouped by tournament
   * Can be sliced by providing (the first two) parameters
   * Search filter will be applied if term is provided via parameter
   * Sorted by tournament-name (in ascending order)
   * Winnings will be currency-formatted
   **/
  rankingsTicketsOnly:
    (state: SeasonRankingState) =>
    (from?: number, to?: number, search = ''): Array<TicketGroup> => {
      try {
        // Spread operator used to clone the original array inside the state
        // console.time('compute table data -> tickets only')
        const filteredList = [...state.current.seasonRankings.filter((item) => item.ticketInfo !== null)]
        let groupedList = [
          ...filteredList.reduce((map, item) => {
            if (map.has(item.ticketInfo.tournamentName)) {
              const items = map.get(item.ticketInfo.tournamentName)
              items.push(item)
              map.set(item.ticketInfo.tournamentName, items)
            } else {
              map.set(item.ticketInfo.tournamentName, [item])
            }
            return map
          }, new Map()),
        ].map(([tournamentName, seasonRankings]) => {
          seasonRankings = seasonRankings.sort(
            (a: SeasonRanking, b: SeasonRanking) => b.ticketInfo.tournamentScore - a.ticketInfo.tournamentScore
          ) as Array<SeasonRankingUI>
          return { tournamentName, seasonRankings }
        })

        // If search term is set in UI
        if (search.length > 0) {
          const searchExpression = new RegExp(search.toLowerCase())
          groupedList = groupedList
            .map((group, index) => {
              group.seasonRankings = group.seasonRankings.filter((ranking: SeasonRanking) =>
                searchExpression.test(ranking.playerName.toLowerCase())
              )
              return group
            })
            .filter((group) => group.seasonRankings.length > 0)
        }
        const result = sliceAndSortRankings(groupedList, from, to, sortTournamentsById) as Array<TicketGroup>
        // console.timeEnd('compute table data -> tickets only')
        return result
      } catch (err) {
        return []
      }
    },
  myRankUi(state: SeasonRankingState, getters: any, rootState: State): SeasonRankingUI {
    const result = {
      ...state.current.myRank,
    }
    ;(result.seasonPrize =
      (result.seasonPrize as any) > 0 ? formatMoneyAmount(result.seasonPrize as string, rootState.user.currency) : '-'),
      (result.seasonRank = (result.seasonRank as any) > 0 ? result.seasonRank : '-')
    return result as SeasonRankingUI
  },
}

const actions = {
  fetch({ commit, getters }: any, zoneId: string) {
    // TODO add cache to not load always the same data
    return fetch(`${process.env.VUE_APP_REST_URL}ranking/season/${zoneId}`, {
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then((res: Response) => {
        if (res.status >= 400) {
          commit('error', { id: 'ranking/season/fetch', responseStatus: res.status }, { root: true })
          throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
        }
        return res.json()
      })
      .then((data: Season) => {
        commit('current', data)
      })
  },
}

const mutations = {
  current(state: SeasonRankingState, season: Season) {
    state.current = season
  },
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
